import type { LatLng } from "leaflet";
import _ from "lodash";
import { type ReactElement, useMemo } from "react";

import { Map } from "@/components";
import type { FloorSpaceCatalog } from "@/models/floor-space-catalog";
import type Light from "@/models/light";
import type MapSettings from "@/models/map-settings";
import type { MarkerPosition } from "@/models/marker-position";
import colors from "@/styles/colors";
import type { FilterableLight, MapMode } from "@/types";
import { createFlagsIcon, createIdIcon, getLightStyle } from "@/utils/map";

import type { MovePoint } from "./draggable-hook";
import { legendZoomThreshold } from "./viewport-culling-hook";

/**
 * Modes that render the global lights and therefore never allow box selection.
 */
export const globalLightModes = [
    "view",
    "add",
    "highlight",
    "group",
    "group-edit",
    "firmware",
];

/**
 * Props for the `useMapElements` hook.
 */
interface MapElementsProps {
    /**
     * Whether the aperture overlay is visible.
     */
    apertureVisible: boolean;
    /**
     * Whether the filter panel is narrowing the visible lights.
     */
    filtering: boolean;
    /**
     * The FloorSpaceCatalog lookup map keyed by floorSpaceCatalogID.
     */
    floorSpaceCatalogMap: Record<number, FloorSpaceCatalog>;
    /**
     * Predicate deciding whether a light falls inside the padded viewport.
     */
    isLightInViewport: (light: Light) => boolean;
    /**
     * Predicate deciding whether a marker falls inside the padded viewport.
     */
    isMarkerInViewport: (marker: MarkerPosition) => boolean;
    /**
     * Whether the identifier legends are currently rendered.
     */
    legendsVisible: boolean;
    /**
     * The light fill opacity as a percentage.
     */
    lightOpacity: number;
    /**
     * The global lights array.
     */
    lights: Light[];
    /**
     * The map settings configuration.
     */
    mapSettings: MapSettings;
    /**
     * The global marker positions keyed by unique ID.
     */
    markers: Record<string, MarkerPosition>;
    /**
     * The current map mode.
     */
    mode: MapMode;
    /**
     * The mode-scoped lights array.
     */
    modeLights: Light[];
    /**
     * The mode-scoped marker positions keyed by unique ID.
     */
    modeMarkers: Record<string, MarkerPosition>;
    /**
     * Called with the new positions after a light drag completes.
     */
    onMoveLight: (points: MovePoint[]) => void;
    /**
     * Called with the new position after a marker drag completes.
     */
    onMoveMarker: (points: MovePoint[]) => void;
    /**
     * Called when a light is clicked.
     */
    onSelectLight: (light: FilterableLight) => void;
    /**
     * Called when a marker is clicked.
     */
    onSelectMarker: (marker: MarkerPosition) => void;
    /**
     * Whether search is narrowing the visible lights.
     */
    searching: boolean;
    /**
     * Toggles the legend marker pane visibility as the zoom threshold is crossed.
     */
    setLegendVisibility: (visible: boolean) => void;
    /**
     * Whether the flag badges are currently rendered.
     */
    showFlag: boolean;
    /**
     * Whether the BLE ID is truncated to its last four characters.
     */
    showLastBleId: boolean;
    /**
     * Snaps a point to the nearest grid position when snapping is enabled.
     */
    snapPoint: (point: LatLng) => LatLng;
}

interface MapElements {
    flags: ReactElement[];
    legends: ReactElement[];
    lights: ReactElement[];
    markers: ReactElement[];
    selectedLights: ReactElement[];
}

/**
 * Builds every Leaflet child of the map in a single pass per collection, applying the
 * mode gate that decides which light and marker collections are rendered.
 *
 * @param params - The collections, viewport predicates, display options, and interaction handlers.
 * @returns The rendered lights, selected lights, legends, flags, and markers.
 */
export const useMapElements = ({
    apertureVisible,
    filtering,
    floorSpaceCatalogMap,
    isLightInViewport,
    isMarkerInViewport,
    legendsVisible,
    lightOpacity,
    lights,
    mapSettings,
    markers,
    mode,
    modeLights,
    modeMarkers,
    onMoveLight,
    onMoveMarker,
    onSelectLight,
    onSelectMarker,
    searching,
    setLegendVisibility,
    showFlag,
    showLastBleId,
    snapPoint,
}: MapElementsProps): MapElements => {
    const { positioningType } = mapSettings.fixtureDetails ?? {};

    const lightElements = useMemo(() => {
        const elements: Omit<MapElements, "markers"> = {
            flags: [],
            legends: [],
            lights: [],
            selectedLights: [],
        };

        const renderLight = (light: Light, defaultColor?: string) => {
            const style = getLightStyle(light, defaultColor, {
                filtering,
                floorSpaceCatalogMap,
                modeName: mode.name,
                positioningType,
                searching,
            });

            return (
                <Map.Light
                    key={light.uniqueId}
                    model={light}
                    aperture={style.aperture}
                    opacity={lightOpacity * 0.01}
                    showSignalAperture={apertureVisible}
                    color={style.color}
                    onSelect={() => onSelectLight(light)}
                    setLegendVisibility={setLegendVisibility}
                    legendZoomThreshold={legendZoomThreshold}
                    onMoveEnd={onMoveLight}
                />
            );
        };

        const renderLegend = (light: Light) => {
            const { position, icon } = createIdIcon(light, {
                positioningType,
                showLastBleId,
            });

            return (
                <Map.Legend
                    key={light._id}
                    position={position}
                    draggable={false}
                    icon={icon}
                    opacity={1}
                />
            );
        };

        const renderFlags = (light: Light) => {
            const flagsIcon = createFlagsIcon(light);

            if (!flagsIcon) return null;

            return (
                <Map.Legend
                    key={light._id}
                    position={flagsIcon.position}
                    draggable={false}
                    icon={flagsIcon.icon}
                    opacity={1}
                />
            );
        };

        const collect = (collection: Light[], defaultColor?: string) =>
            _.forEach(collection, (light) => {
                if (!light.selected && !isLightInViewport(light)) return;

                if (light.selected) {
                    elements.selectedLights.push(renderLight(light));
                } else {
                    elements.lights.push(renderLight(light, defaultColor));
                }

                if (legendsVisible) {
                    elements.legends.push(renderLegend(light));
                }

                if (showFlag) {
                    const flags = renderFlags(light);

                    if (flags) elements.flags.push(flags);
                }
            });

        if (globalLightModes.includes(mode.name)) {
            collect(lights);
        }

        if (mode.name !== "view") {
            collect(
                modeLights,
                mode.name === "add" ? colors.attentionYellow : undefined,
            );
        }

        return elements;
    }, [
        apertureVisible,
        filtering,
        floorSpaceCatalogMap,
        isLightInViewport,
        legendsVisible,
        lightOpacity,
        lights,
        mode.name,
        modeLights,
        onMoveLight,
        onSelectLight,
        positioningType,
        searching,
        setLegendVisibility,
        showFlag,
        showLastBleId,
    ]);

    const markerElements = useMemo(() => {
        const renderMarker = (marker: MarkerPosition) => (
            <Map.Marker
                key={marker.uniqueId}
                model={marker}
                onSelect={() => onSelectMarker(marker)}
                onMoveEnd={onMoveMarker}
                snapPoint={snapPoint}
            />
        );

        const elements: ReactElement[] = [];

        if (mode.name !== "select") {
            for (const marker of Object.values(markers)) {
                if (isMarkerInViewport(marker)) {
                    elements.push(renderMarker(marker));
                }
            }
        }

        for (const marker of Object.values(modeMarkers)) {
            elements.push(renderMarker(marker));
        }

        return elements;
    }, [
        isMarkerInViewport,
        markers,
        mode.name,
        modeMarkers,
        onMoveMarker,
        onSelectMarker,
        snapPoint,
    ]);

    return { ...lightElements, markers: markerElements };
};
